import React, { useMemo, useState } from 'react';
import { Input, Button, notification, Space, Typography, Select } from 'antd';
import { database, ref, push, set } from '../../pages/Auth/db/firebase';

const { TextArea } = Input;
const { Text } = Typography;

const SUBJECT_OPTIONS = [
  {
    value: 'request-demo',
    label: { en: 'Request a demo', fr: 'Demander une démonstration' }
  },
  {
    value: 'general-inquiry',
    label: { en: 'General inquiry', fr: 'Question générale' }
  },
  {
    value: 'pricing',
    label: { en: 'Pricing question', fr: 'Question sur les tarifs' }
  },
  {
    value: 'support',
    label: { en: 'Need support', fr: "Besoin d'assistance" }
  },
  {
    value: 'partnership',
    label: { en: 'Partnership opportunity', fr: 'Opportunité de partenariat' }
  }
];

const COPY = {
  en: {
    labels: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone (optional)',
      subject: 'Reason for contacting',
      message: 'How can we help?'
    },
    placeholders: {
      name: 'Your full name',
      email: 'name@company.com',
      phone: 'Include country code if outside your region',
      subject: 'Choose a topic',
      message: 'Tell us about your goals, challenges or what you’d like to see.'
    },
    cta: 'Send Message',
    helper: 'We’ll be in touch very soon.',
    validation: {
      name: 'Please enter your name',
      email: 'Please enter a valid email',
      subject: 'Please select a reason',
      message: 'Please enter a message'
    },
    success: {
      title: 'Message sent',
      description: 'Thanks! We’ll be in touch soon.'
    },
    error: {
      title: 'Send failed',
      description: 'Unable to send message'
    }
  },
  fr: {
    labels: {
      name: 'Nom',
      email: 'Email',
      phone: 'Téléphone (optionnel)',
      subject: 'Raison du contact',
      message: 'Comment pouvons-nous vous aider ?'
    },
    placeholders: {
      name: 'Votre nom complet',
      email: 'nom@entreprise.com',
      phone: 'Incluez l’indicatif si vous êtes à l’étranger',
      subject: 'Choisissez un sujet',
      message: 'Parlez-nous de vos objectifs, défis ou besoins.'
    },
    cta: 'Envoyer le message',
    helper: 'Nous vous répondrons très bientôt.',
    validation: {
      name: 'Veuillez entrer votre nom',
      email: 'Veuillez entrer un email valide',
      subject: 'Veuillez choisir une raison',
      message: 'Veuillez entrer un message'
    },
    success: {
      title: 'Message envoyé',
      description: 'Merci ! Nous vous recontacterons rapidement.'
    },
    error: {
      title: 'Échec de l’envoi',
      description: 'Impossible d’envoyer le message'
    }
  }
};

const ContactForm = ({ language = 'en' }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const copy = COPY[language] || COPY.en;
  const subjectOptions = useMemo(
    () => SUBJECT_OPTIONS.map((option) => ({ value: option.value, label: option.label[language] })),
    [language]
  );

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) {
      api.warning({ message: copy.validation.name });
      return false;
    }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      api.warning({ message: copy.validation.email });
      return false;
    }
    if (!form.subject) {
      api.warning({ message: copy.validation.subject });
      return false;
    }
    if (!form.message.trim()) {
      api.warning({ message: copy.validation.message });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const timestamp = new Date().toISOString();
      const senderName = form.name.trim();
      const senderEmail = form.email.trim();
      const senderPhone = form.phone.trim();
      const body = form.message.trim();
      const selectedSubject = SUBJECT_OPTIONS.find((option) => option.value === form.subject);

      const messagesRef = ref(database, 'messages/me');
      const newRef = push(messagesRef);

      await set(newRef, {
        from: 'public',
        to: 'superadmin',
        subject: selectedSubject
          ? `NEW User ** - ${selectedSubject.label.en}`
          : 'NEW User **',
        requestType: selectedSubject?.value || null,
        requestLabel: selectedSubject?.label.en || null,
        requestLabelFr: selectedSubject?.label.fr || null,
        message: body,
        senderName,
        senderEmail,
        senderPhone: senderPhone || null,
        contactType: 'landing-request',
        summary: `Inbound request from ${senderName} (${senderEmail})`,
        language,
        timestamp,
        status: 'unread'
      });

      api.success({
        message: copy.success.title,
        description: copy.success.description
      });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      api.error({ message: copy.error.title, description: err.message || copy.error.description });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-form-panel" style={{ maxWidth: 760 }}>
      {contextHolder}
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{copy.labels.name}</Text>
          <Input
            size="large"
            placeholder={copy.placeholders.name}
            value={form.name}
            onChange={handleChange('name')}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{copy.labels.email}</Text>
          <Input
            size="large"
            placeholder={copy.placeholders.email}
            value={form.email}
            onChange={handleChange('email')}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{copy.labels.phone}</Text>
          <Input
            size="large"
            placeholder={copy.placeholders.phone}
            value={form.phone}
            onChange={handleChange('phone')}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{copy.labels.subject}</Text>
          <Select
            size="large"
            placeholder={copy.placeholders.subject}
            value={form.subject || undefined}
            onChange={(value) => setForm({ ...form, subject: value })}
            options={subjectOptions}
            style={{ width: '100%' }}
            popupClassName="contact-select-dropdown"
            dropdownMatchSelectWidth={false}
          />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>{copy.labels.message}</Text>
          <TextArea
            rows={6}
            placeholder={copy.placeholders.message}
            value={form.message}
            onChange={handleChange('message')}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12, maxWidth: 420 }}>
            {copy.helper}
          </Text>
          <Button type="primary" size="large" onClick={handleSubmit} loading={loading}>
            {copy.cta}
          </Button>
        </div>
      </Space>
    </div>
  );
};

export default ContactForm;
